// @workflow assistant.voice
/**
 * Read aloud's endpoint (contract "Read aloud"): POST
 * api/assistant/conversations/{conversationId}/messages/{messageId}/speech, no body, through
 * authedFetch.js (auth headers, one resend after a 401 refresh). The ids are the UI's conversation
 * id (the server's ClientConversationId, as for feedback) and the reply's id from message.start.
 *
 * Returns the 200 response so the player can read the MP3 as it streams. A refusal throws a
 * ReadAloudError with the contract code; a network failure is `speech.failed`; an abort rethrows.
 * No Vue or path-alias imports.
 */
import { READ_ALOUD_CODE, codeForSpeechRefusal } from '../domain/readAloudCodes.js';
import { createAuthedFetch, isAbortError, readJson } from './authedFetch.js';

/** Audio when it works; a refusal comes back as JSON with its code. */
const ACCEPT = 'audio/mpeg, application/json';

export class ReadAloudError extends Error {
  /** @param {string} code a READ_ALOUD_CODE */
  constructor(code) {
    super(`Read aloud refused: ${code}`);
    this.name = 'ReadAloudError';
    this.code = code;
  }
}

/**
 * @param {{ baseUrl: string, fetchImpl?: typeof fetch, getHeaders?: () => Record<string, string>, refreshAuth?: () => Promise<boolean> }} deps
 */
export function createReadAloudClient({ baseUrl, fetchImpl, getHeaders, refreshAuth }) {
  const api = `${String(baseUrl || '').replace(/\/+$/, '')}/api/assistant/conversations`;
  const authed = createAuthedFetch({ fetchImpl, getHeaders, refreshAuth });

  return {
    /**
     * @param {{ conversationId: string, messageId: string, signal?: AbortSignal }} reply
     * @returns {Promise<Response>} the streamed audio/mpeg response
     */
    async speech({ conversationId, messageId, signal }) {
      const url = `${api}/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/speech`;
      let response;
      try {
        response = await authed(url, { method: 'POST', signal, accept: ACCEPT });
      } catch (err) {
        if (isAbortError(err, signal)) throw err;
        throw new ReadAloudError(READ_ALOUD_CODE.FAILED);
      }
      if (!response.ok) throw new ReadAloudError(codeForSpeechRefusal(response.status, await readJson(response)));
      return response;
    },
  };
}
